import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { Observable } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {TransactionPagedList} from 'src/swagger-gen/web-secured/models';
import { BaseService } from '../services/base.service';
import {TransactionSearchBody} from '../../swagger-gen/vnf-secured/models/transaction-search-body';

@Injectable({
  providedIn: 'root',
})
export class TransactionRepo extends BaseService {
  readonly customExportTransactionsPath = '/custom/transactions/export';
  readonly exportTransactionsPath = '/transactions/export';
  readonly TRANSACTION_LIMITS = '200';

  baseUrl = environment.apiUrl;
  stationUrl = this.baseUrl + '/stations';
  customStationUrl = this.baseUrl + '/custom/stations';

  constructor(private http: HttpClient, private logger: NGXLogger) {
    super();
  }

  /**
   * Get all transactions
   * @param params search params
   * @param offset offset
   * @param limit limit
   * @returns transaction list observable
   */
  getTransactions(
    params: any[],
    offset?: number,
    limit?: number
  ): Observable<TransactionPagedList> {
    const url = `${this.baseUrl}/transactions/search`;
    let requestString = this.pagination(offset, limit, this.TRANSACTION_LIMITS);

    let queryParams = '';
    params.forEach((p: any) => {
      queryParams += '&' + p.key + '=' + p.value;
    });

    requestString += queryParams;

    const requestParams = new HttpParams({ fromString: requestString });

    const httpOptions = {
      headers: this.httpHeaders(),
      params: requestParams,
    };

    return this.http.post<TransactionPagedList>(url, null, httpOptions).pipe(
      tap((response) => {
        this.logger.log(`Get transaction List from backend`, response);
      }), // DEBUG
      catchError(this.handleServerError)
    );
  }

  /**
   * Get transaction list of a station
   * @param eUid station uid
   * @param params search params
   * @param body
   * @param offset offset
   * @param limit limit
   * @returns Observable list
   */
  getStationTransactions(
    eUid: string,
    params: any[],
    body: TransactionSearchBody,
    offset?: number,
    limit?: number
  ): Observable<TransactionPagedList> {
    const url = `${this.customStationUrl}/${eUid}/transactions/search`;
    let requestString = this.pagination(offset as number, limit as number, this.TRANSACTION_LIMITS);

    let queryParams = '';
    params.forEach((p: any) => {
      queryParams += '&' + p.key + '=' + p.value;
    });

    requestString += queryParams;

    const requestParams = new HttpParams({ fromString: requestString });

    const httpOptions = {
      headers: this.httpHeaders(),
      params: requestParams,
    };

    return this.http.post<TransactionPagedList>(url, body, httpOptions).pipe(
      tap((response) => {
        this.logger.log(`Get transaction List from backend`, response);
      }), // DEBUG
      catchError(this.handleServerError)
    );
  }

  /**
   * Allows to export all the transactions as an XLSX File.
   * @param params
   * - `startDate`: The start date time of data to filter
   * - `endDate`: The end date time of data to filter
   * @return Success
   */
  exportTransactions(params: any[]): Observable<any> {
    let queryParams = '';
    params.forEach((p: any, i: number) => {
      queryParams += `${i > 0 ? '&' : ''}` + p.key + 'Time=' + p.value;
    });

    const url = `${this.baseUrl}${this.customExportTransactionsPath}`;
    const requestParams = new HttpParams({ fromString: queryParams });

    let headers = new HttpHeaders();
    headers = headers.set('Content-Type', 'application/json');

    const httpOptions: any = {
      headers: headers,
      params: requestParams,
      responseType: 'blob' as 'json',
      observe: 'response',
    };

    return this.http.post<any>(url, {}, httpOptions).pipe(
      tap((data) => {
        this.logger.trace(`Download all transactions:`, data);
      }), // DEBUG
      catchError(this.handleServerError)
    );
  }

  customExportTransactions(stationUid: string, params: any[]): Observable<any> {
    let queryParams = '';
    params.forEach((p: any, i: number) => {
      queryParams += `${i > 0 ? '&' : ''}` + p.key + '=' + p.value;
    });

    const url = `${this.customStationUrl}/${stationUid}${this.exportTransactionsPath}`;
    const requestParams = new HttpParams({ fromString: queryParams });

    let headers = new HttpHeaders();
    headers = headers.set('Content-Type', 'application/json');

    const httpOptions: any = {
      headers: headers,
      params: requestParams,
      responseType: 'blob' as 'json',
      observe: 'response',
    };

    return this.http.post<any>(url, {}, httpOptions).pipe(
        tap((data) => {
          this.logger.trace(`Download all transactions:`, data);
        }), // DEBUG
        catchError(this.handleServerError)
    );
  }

  /**
   * Download invoice by its uid
   * @param uid transaction uid
   */
  downloadInvoice(uid: string): Observable<any> {
    const url = `${this.baseUrl}/transactions/${uid}/invoice`;
    let headers = new HttpHeaders();
    headers = headers.set('Content-Type', 'application/json');
    const httpOptions: any = {
      headers: headers,
      responseType: 'blob' as 'json',
      observe: 'response',
    };
    return this.http.get<any>(url, httpOptions).pipe(
      tap((data) => {
        this.logger.trace(`Download invoice:`, data);
      }), // DEBUG
      catchError(this.handleServerError)
    );
  }
}
